import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface ItemsKgReportParams {
  designatedOperator: string;
  direction: 'Inbound' | 'Outbound';
  fromYear: number;
  fromQuarter: number;
  toYear: number;
  toQuarter: number;
  unex: boolean;
  formatType: 'html' | 'xls';
}

export interface ItemsKgReportResponse {
  // For HTML format, contains the raw HTML response data
  data?: any[];
  url?: string;
  content?: Blob;
  filename?: string;
}

export function useItemsKgReport() {
  return useMutation<ItemsKgReportResponse, Error, ItemsKgReportParams>({
    mutationFn: async (params: ItemsKgReportParams) => {
      // Create URLSearchParams for the request
      const searchParams = new URLSearchParams();
      
      // Add required parameters
      // Only add designatedOperator if it's not "ALL"
      if (params.designatedOperator !== 'ALL') {
        searchParams.append('designatedOperator', params.designatedOperator);
      }
      searchParams.append('direction', params.direction);
      searchParams.append('fromYear', params.fromYear.toString());
      searchParams.append('fromQuarter', params.fromQuarter.toString());
      searchParams.append('toYear', params.toYear.toString());
      searchParams.append('toQuarter', params.toQuarter.toString());
      searchParams.append('unex', params.unex.toString());
      searchParams.append('formatType', params.formatType);

      const response = await axios.get(`/reports/ipk?${searchParams.toString()}`, {
        // HTML returns JSON with table data, XLS returns binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `ItemsKg_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 